import React, { useState } from "react";
import { Edit2, Printer } from "lucide-react";
import {
  PatientInfo as PatientInfoType,
  usePatientFormState,
} from "@/lib/dashboard/patientDashboardData";
import { getPatientInfoItems } from "@/lib/dashboard/patientProfileConfig";
import {
  getPatientByNhsNumber,
  getPatientByDemoGraphics,
  setCurrentShowPatientDetailsEditModal,
} from "@/dispatchers/patientDispatchers";
import { Skeleton } from "@mui/material";
import { NhsServicesMenu } from "../../NhsServicesMenu";
import useTitanApp from "@/hooks/useTitanApp";
import { PdsSyncButton } from "./PdsSyncButton";
import { PdsSyncDialog } from "./pdsSync/PdsSyncDialog";

interface PatientInfoProps {
  patientData: PatientInfoType;
  isDarkMode: boolean;
  onEditPatient?: () => void;
  onPrintSummary: () => void;
}

export function PatientInfo({
  patientData,
  isDarkMode,
  onEditPatient,
  onPrintSummary,
}: PatientInfoProps) {
  const [isPdsDialogOpen, setIsPdsDialogOpen] = useState(false);
  const [isPdsLoading, setIsPdsLoading] = useState(false);
  const [pdsRecordData, setPdsRecordData] = useState<any>(null);
  const infoItems = getPatientInfoItems(patientData);
  const { isPatientDetailsLoading } = usePatientFormState();
  const { appState, pharmacyServiceState } = useTitanApp();
  const { allPharmacies } = pharmacyServiceState;

  const { currentPharmacy, currentPharmacyId } = appState || {};
  const matchedPharmacy =
    allPharmacies?.find(
      (pharmacy: any) =>
        (pharmacy?.teamPharmacyID || pharmacy?.pharmacyID) === currentPharmacyId,
    ) || null;

  const requestingOdsCode =
    matchedPharmacy?.odsCode ||
    matchedPharmacy?.ODSCode ||
    matchedPharmacy?.ods ||
    "";

  const handlePdsSyncClick = async () => {
    setIsPdsDialogOpen(true);
    setIsPdsLoading(true);
    try {
      const pharmacyId = currentPharmacyId || "";
      let resp: any = null;
      if (patientData?.nhsNumber && String(patientData.nhsNumber).trim() !== "") {
        const data = {
          nhsNumber: String(patientData.nhsNumber).trim(),
          pharmacyId: pharmacyId,
        };
        resp = await getPatientByNhsNumber(data);
      } else {
        const data = {
          givenName: (patientData as any)?.foreName || "",
          familyName: (patientData as any)?.surName || "",
          gender: Number((patientData as any)?.sex) || 0,
          dateOfBirth: (patientData as any)?.dob || "",
          pharmacyId: pharmacyId,
        };
        resp = await getPatientByDemoGraphics(data);
      }
      setPdsRecordData(resp);
    } catch (error) {
      console.error("Error fetching PDS patient data:", error);
    } finally {
      setIsPdsLoading(false);
    }
  };

  const renderValue = (value: string) => {
    if (isPatientDetailsLoading) {
      return (
        <Skeleton
          variant="rectangular"
          animation="wave"
          height={12}
          width="80%"
          sx={{
            borderRadius: "3px",
            bgcolor: isDarkMode ? "grey.700" : "grey.300",
          }}
        />
      );
    }
    return value || "-";
  };
  function handleEditPatient() {
    setCurrentShowPatientDetailsEditModal(true);
  }

  return (
    <>
      {/* Details Header with Actions */}
      <div className="flex items-center justify-between mb-3">
        <h3
          className={`text-sm font-semibold transition-colors duration-300 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Details
        </h3>
        <div className="flex items-center gap-2">
          <PdsSyncButton
            isDarkMode={isDarkMode}
            onClick={handlePdsSyncClick}
          />
          {/* <NhsServicesMenu
            nhsNumber={patientData?.nhsNumber}
            requestingOdsCode={requestingOdsCode}
          /> */}
          <button
            onClick={onPrintSummary}
            className={`p-0 h-auto font-normal transition-colors duration-300 ${
              isDarkMode
                ? "text-gray-400 hover:text-gray-200"
                : "text-gray-500 hover:text-gray-700"
            }`}
            title="Print Summary"
          >
            <Printer className="w-4 h-4" />
          </button>
          <button
            onClick={handleEditPatient}
            className={`p-0 h-auto font-normal transition-colors duration-300 ${
              isDarkMode
                ? "text-gray-400 hover:text-gray-200"
                : "text-gray-500 hover:text-gray-700"
            }`}
            title="Edit Patient"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Dual-Column Patient Info */}
      <div
        className={`${
          isDarkMode
            ? "bg-gray-700 border-gray-600"
            : "bg-white border-gray-200"
        } rounded-lg border p-3 mb-4 transition-colors duration-300`}
      >
        {/* First 4 items in 2x2 grid */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 mb-3">
          {infoItems?.slice(0, 4).map((item, index) => {
            const IconComponent = item.icon;
            return (
              <div key={index} className="flex items-start gap-2 min-w-0">
                <IconComponent
                  className={`w-3 h-3 mt-0.5 flex-shrink-0 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <span
                    className={`text-xs ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {item.label}
                  </span>
                  <div
                    className={`text-xs font-medium leading-tight ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {renderValue(item.value)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Email and Address full-width (longer content) */}
        <div
          className={`space-y-2 pt-2 border-t ${
            isDarkMode ? "border-gray-600" : "border-gray-200"
          }`}
        >
          {infoItems.slice(4).map((item, index) => {
            const IconComponent = item.icon;
            return (
              <div key={index + 4} className="flex items-start gap-2">
                <IconComponent
                  className={`w-3 h-3 mt-0.5 flex-shrink-0 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <span
                    className={`text-xs ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {item.label}
                  </span>
                  <div
                    className={`text-xs font-medium leading-tight ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {renderValue(item.value)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <PdsSyncDialog
        open={isPdsDialogOpen}
        onOpenChange={setIsPdsDialogOpen}
        localRecord={patientData as any}
        pdsRecord={pdsRecordData}
        isLoading={isPdsLoading}
        onDarkMode={isDarkMode}
      />
    </>
  );
}
